/**
 * MobileStandaloneApp.jsx
 * Standalone fullscreen mobile app - runs at /mobile route
 * Reuses all mobile-preview screens without PhoneFrame wrapper
 * Ready for Capacitor native packaging
 */
import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { useTranslation } from 'react-i18next';
import '../i18n';
import { initCapacitor, isNative, platform } from '../utils/capacitor';
import { initPushNotifications, removePushToken } from '../utils/pushNotifications';
import {
  RefreshCw, Home, Calendar, Utensils, Heart, Menu, Plus,
  Camera, Search, Sparkles, X
} from 'lucide-react';

// Eagerly imported modals (needed for FAB overlay)
import FoodSearchModal from '../components/admin/mobile-preview/screens/FoodSearchModal';
import BarcodeScanner from '../components/admin/mobile-preview/screens/BarcodeScanner';
import MobileLegalModal from '../components/admin/mobile-preview/screens/MobileLegalModal';
import MealPhotoAnalyzer from '../components/admin/mobile-preview/screens/MealPhotoAnalyzer';
import CreateMealModal from '../components/admin/mobile-preview/screens/CreateMealModal';
import CreateProductWizard from '../components/admin/mobile-preview/screens/CreateProductWizard';
import MealPlannerScreen from '../components/admin/mobile-preview/screens/MealPlannerScreen';
import VoiceInputModal from '../components/admin/mobile-preview/screens/VoiceInputModal';
import MealCalculatorModal from '../components/admin/mobile-preview/screens/MealCalculatorModal';

// Seasonal effects
import SnowEffect from '../components/admin/mobile-preview/SnowEffect';
import ConfettiEffect from '../components/admin/mobile-preview/ConfettiEffect';
import CherryBlossomEffect from '../components/admin/mobile-preview/CherryBlossomEffect';
import FireworksEffect from '../components/admin/mobile-preview/FireworksEffect';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Lazy-loaded screens
const lazyScreen = (importFn, exportName) => lazy(() =>
  importFn().then(module => ({ default: module[exportName] }))
);

const SplashScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/SplashScreen'), 'SplashScreen');
const LoginScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/LoginScreen'), 'LoginScreen');
const HomeScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/HomeScreen'), 'HomeScreen');
const NutritionScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/NutritionScreen'), 'NutritionScreen');
const BookingsScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/BookingsScreen'), 'BookingsScreen');
const BodyScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/BodyScreen'), 'BodyScreen');
const ProfileScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/ProfileScreen'), 'ProfileScreen');
const AccountScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/AccountScreen'), 'AccountScreen');
const ShopScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/ShopScreen'), 'ShopScreen');
const HealthScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/HealthScreen'), 'HealthScreen');
const SleepScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/SleepScreen'), 'SleepScreen');
const CommunityScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/CommunityScreen'), 'CommunityScreen');
const RecipesScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/RecipesScreen'), 'RecipesScreen');
const ShoppingScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/ShoppingListScreen'), 'ShoppingScreen');
const BookingNewScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/BookingNewScreen'), 'BookingNewScreen');
const WaitlistScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/WaitlistScreen'), 'WaitlistScreen');
const FitnessScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/FitnessScreen'), 'FitnessScreen');
const CustomerProfileScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/CustomerProfileScreen'), 'CustomerProfileScreen');
const HealthCoachScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/HealthCoachScreen'), 'HealthCoachScreen');
const DocumentsScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/DocumentsScreen'), 'DocumentsScreen');
const PeriodizationScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/PeriodizationScreen'), 'PeriodizationScreen');
const RecoveryScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/RecoveryScreen'), 'RecoveryScreen');
const TransactionHistoryScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/TransactionHistoryScreen'), 'TransactionHistoryScreen');
const NotificationSettingsScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/NotificationSettingsScreen'), 'NotificationSettingsScreen');
const SubscriptionsScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/SubscriptionsScreen'), 'SubscriptionsScreen');
const TrainingAnalyticsScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/TrainingAnalyticsScreen'), 'TrainingAnalyticsScreen');
const PhotoHistoryScreen = lazy(() => import('../components/admin/mobile-preview/screens/PhotoHistoryScreen'));
const AppSettingsScreen = lazyScreen(() => import('../components/admin/mobile-preview/screens/AppSettingsScreen'), 'AppSettingsScreen');

// Eagerly imported feedback (overlay, not a route)
import FeedbackScreen from '../components/admin/mobile-preview/screens/FeedbackScreen';

const ScreenLoader = () => (
  <div className="flex items-center justify-center h-full" data-testid="mobile-screen-loader">
    <RefreshCw className="w-8 h-8 animate-spin text-yellow-500" />
  </div>
);

export default function MobileStandaloneApp() {
  const { t, i18n } = useTranslation();
  const [currentScreen, setCurrentScreen] = useState('splash');
  const [selectedTab, setSelectedTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [fabMenuOpen, setFabMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Portal Token
  const [portalToken, setPortalToken] = useState(null);

  // Customer data
  const [customerData, setCustomerData] = useState(null);

  // Bidirektionaler Sprach-Sync (06/2026): preferred_language aus dem
  // Kundenprofil (Quelle der Wahrheit) automatisch übernehmen.
  const applyProfileLanguage = useCallback((lang) => {
    if (lang && ['de', 'en', 'fr', 'it'].includes(lang) && i18n.language !== lang) {
      localStorage.setItem('mobile_app_language', lang);
      i18n.changeLanguage(lang);
    }
  }, [i18n]);

  useEffect(() => {
    applyProfileLanguage(customerData?.customer?.preferred_language);
  }, [customerData?.customer?.preferred_language, applyProfileLanguage]);
  const [appointments, setAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [pointsData, setPointsData] = useState({ balance: 0, credit_balance: 0, transactions: [] });
  const [packages, setPackages] = useState([]);
  const [bodyData, setBodyData] = useState({ entries: [], latest: null, progress: null });
  const [bodyLoading, setBodyLoading] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [services, setServices] = useState([]);
  const [trainers, setTrainers] = useState([]);

  const [dataLoaded, setDataLoaded] = useState(false);
  const [appSettings, setAppSettings] = useState(null);

  // Legal
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalStatus, setLegalStatus] = useState(null);

  // Nutrition modals
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showPhotoAnalyzer, setShowPhotoAnalyzer] = useState(false);
  const [showCreateMeal, setShowCreateMeal] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [createProductBarcode, setCreateProductBarcode] = useState('');
  const [showVoiceInput, setShowVoiceInput] = useState(false);
  const [showMealPlanner, setShowMealPlanner] = useState(false);
  const [showMealCalculator, setShowMealCalculator] = useState(false);
  const [nutritionMealType, setNutritionMealType] = useState('lunch');
  const [nutritionRefreshKey, setNutritionRefreshKey] = useState(0);

  // Nutrition access
  const [nutritionAccess, setNutritionAccess] = useState({ has_access: true });

  // Feedback
  const [pendingFeedback, setPendingFeedback] = useState([]);
  const [feedbackAppointment, setFeedbackAppointment] = useState(null);

  // Seasonal effects
  const isInDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    const today = new Date();
    const currentMonthDay = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    if (startDate > endDate) return currentMonthDay >= startDate || currentMonthDay <= endDate;
    return currentMonthDay >= startDate && currentMonthDay <= endDate;
  };

  const snowEnabled = appSettings?.seasonal_effects?.snow_enabled ||
    (appSettings?.seasonal_effects?.snow_auto_enabled &&
      isInDateRange(appSettings?.seasonal_effects?.snow_start_date, appSettings?.seasonal_effects?.snow_end_date));
  const snowIntensity = appSettings?.seasonal_effects?.snow_intensity || 'medium';
  const snowSpeed = appSettings?.seasonal_effects?.snow_speed || 'medium';
  const confettiEnabled = appSettings?.seasonal_effects?.confetti_enabled ||
    (appSettings?.seasonal_effects?.confetti_auto_enabled &&
      isInDateRange(appSettings?.seasonal_effects?.confetti_start_date || '12-31', appSettings?.seasonal_effects?.confetti_end_date || '01-02'));
  const confettiIntensity = appSettings?.seasonal_effects?.confetti_intensity || 'medium';
  const cherryBlossomEnabled = appSettings?.seasonal_effects?.cherry_blossom_enabled ||
    (appSettings?.seasonal_effects?.cherry_blossom_auto_enabled &&
      isInDateRange(appSettings?.seasonal_effects?.cherry_blossom_start_date || '03-20', appSettings?.seasonal_effects?.cherry_blossom_end_date || '04-30'));
  const cherryBlossomIntensity = appSettings?.seasonal_effects?.cherry_blossom_intensity || 'medium';
  const fireworksEnabled = appSettings?.seasonal_effects?.fireworks_enabled ||
    (appSettings?.seasonal_effects?.fireworks_auto_enabled &&
      isInDateRange(appSettings?.seasonal_effects?.fireworks_start_date || '12-31', appSettings?.seasonal_effects?.fireworks_end_date || '01-01'));
  const fireworksIntensity = appSettings?.seasonal_effects?.fireworks_intensity || 'medium';
  const winterHatEnabled = appSettings?.seasonal_effects?.winter_hat_enabled && snowEnabled;

  // Initialize Capacitor and load app settings on mount
  useEffect(() => {
    initCapacitor();
    loadAppSettings();
  }, []);

  const loadAppSettings = async () => {
    try {
      const res = await axios.get(`${API}/portal/app-settings`).catch(() => ({ data: {} }));
      setAppSettings(res.data);
    } catch (e) {
      console.error('Error loading app settings:', e);
    } finally {
      setLoading(false);
    }
  };

  // Restore session from localStorage
  useEffect(() => {
    const savedToken = localStorage.getItem('mobile_portal_token');
    const savedCustomer = localStorage.getItem('mobile_customer_data');
    if (savedToken && savedCustomer) {
      try {
        const customer = JSON.parse(savedCustomer);
        setPortalToken(savedToken);
        setCustomerData(customer);
        setIsLoggedIn(true);
        setCurrentScreen('home');
        setSelectedTab('home');
        loadCustomerData(savedToken);
        checkLegalStatus(savedToken);
        loadPendingFeedback(savedToken);
        axios
          .get(`${API}/portal/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } })
          .then((r) => applyProfileLanguage(r.data?.preferred_language))
          .catch(() => {});
        // Init push for auto-login (customer ID from stored data)
        const storedCustomer = JSON.parse(localStorage.getItem('mobile_customer_data') || '{}');
        const cid = storedCustomer?.id || storedCustomer?.customer_id;
        if (cid) initPushNotifications(savedToken, cid);
      } catch (e) {
        localStorage.removeItem('mobile_portal_token');
        localStorage.removeItem('mobile_customer_data');
      }
    }
  }, []);

  // Check legal status
  const checkLegalStatus = async (customerToken) => {
    if (!customerToken) return;
    try {
      const response = await axios.get(`${API}/portal/auth/legal-check`, {
        headers: { Authorization: `Bearer ${customerToken}` }
      });
      setLegalStatus(response.data);
      if (response.data.needs_acceptance) setShowLegalModal(true);
    } catch (error) {
      console.error('Error checking legal status:', error);
    }
  };

  const handleAcceptLegal = async (acceptedCustomConditions = []) => {
    if (!portalToken) return;
    try {
      await axios.post(`${API}/portal/auth/accept-legal`,
        { accepted_custom_conditions: acceptedCustomConditions },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setShowLegalModal(false);
      setLegalStatus(null);
      toast.success('Bedingungen akzeptiert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Load pending feedback
  const loadPendingFeedback = async (customerToken) => {
    if (!customerToken) return;
    try {
      const res = await axios.get(`${API}/portal/feedback/pending`, {
        headers: { Authorization: `Bearer ${customerToken}` }
      });
      setPendingFeedback(res.data.pending || []);
    } catch {
      setPendingFeedback([]);
    }
  };

  // Load customer data
  const loadCustomerData = useCallback(async (customerToken) => {
    if (!customerToken) return;
    const headers = { Authorization: `Bearer ${customerToken}` };
    try {
      const [
        appointmentsRes, upcomingRes, invoicesRes, pointsRes,
        packagesRes, servicesRes, trainersRes,
        bodyEntriesRes, bodyLatestRes, bodyProgressRes
      ] = await Promise.all([
        axios.get(`${API}/portal/appointments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/appointments/upcoming`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/invoices`, { headers }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API}/portal/points`, { headers }).catch(() => ({ data: { balance: 0, credit_balance: 0, transactions: [] } })),
        axios.get(`${API}/portal/packages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/shop/services`, { headers }).catch(() => ({ data: { services: [] } })),
        axios.get(`${API}/portal/booking/trainers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/body/weight`, { headers }).catch(() => ({ data: { entries: [] } })),
        axios.get(`${API}/portal/body/measurements`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/portal/body/progress`, { headers }).catch(() => ({ data: { has_data: false } })),
      ]);

      setAppointments(appointmentsRes.data || []);
      setUpcomingAppointments(upcomingRes.data || []);
      setInvoices(invoicesRes.data.invoices || invoicesRes.data || []);
      setPointsData(pointsRes.data);
      setPackages(packagesRes.data || []);
      setServices(servicesRes.data.services || servicesRes.data || []);
      setTrainers(trainersRes.data.trainers || trainersRes.data || []);

      const weightEntries = Array.isArray(bodyEntriesRes.data)
        ? bodyEntriesRes.data
        : (bodyEntriesRes.data?.entries || []);
      const latestWeight = weightEntries.length > 0 ? weightEntries[0] : null;
      const measurements = bodyLatestRes.data || {};

      setBodyData({
        entries: weightEntries,
        latest: {
          weight: latestWeight?.weight,
          bmi: latestWeight?.bmi,
          height: latestWeight?.height || measurements.height,
          muscle_mass: latestWeight?.muscle_mass,
          body_fat: latestWeight?.body_fat_percent || latestWeight?.body_fat,
          water_percent: latestWeight?.water_percent,
          visceral_fat: latestWeight?.visceral_fat
        },
        progress: bodyProgressRes.data
      });

      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  }, []);

  const refreshData = async () => {
    if (portalToken) {
      toast.info(t('data.refreshing'));
      await loadCustomerData(portalToken);
      toast.success(t('data.refreshed'));
    }
  };

  // Login
  const handleAppLogin = async (email, password) => {
    setLoginError('');
    try {
      const loginRes = await axios.post(`${API}/portal/auth/login`, { email, password });
      if (loginRes.data.token && loginRes.data.customer) {
        const customerToken = loginRes.data.token;
        setPortalToken(customerToken);
        const customer = {
          customer: loginRes.data.customer,
          points: loginRes.data.customer.points || 0,
          credit_balance: loginRes.data.customer.credit_balance || 0
        };
        setCustomerData(customer);
        setNutritionAccess(loginRes.data.customer.nutrition_access || { has_access: true });

        // Persist session
        localStorage.setItem('mobile_portal_token', customerToken);
        localStorage.setItem('mobile_customer_data', JSON.stringify(customer));

        await loadCustomerData(customerToken);
        await checkLegalStatus(customerToken);
        loadPendingFeedback(customerToken);

        // Initialize push notifications on native platforms
        const cid = loginRes.data.customer.id || loginRes.data.customer.customer_id;
        initPushNotifications(customerToken, cid);

        toast.success(t('auth.welcome_back', { name: loginRes.data.customer.first_name }));
        setIsLoggedIn(true);
        setCurrentScreen('home');
        setSelectedTab('home');
        return true;
      }
      throw new Error('Ungültige Antwort');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Login fehlgeschlagen';
      setLoginError(errorMsg);
      return false;
    }
  };

  // Logout
  const handleLogout = () => {
    // Cleanup push notifications
    const cid = customerData?.id || customerData?.customer_id;
    if (cid) removePushToken(cid);

    setPortalToken(null);
    setCustomerData(null);
    setIsLoggedIn(false);
    setDataLoaded(false);
    setCurrentScreen('login');
    setSelectedTab('home');
    localStorage.removeItem('mobile_portal_token');
    localStorage.removeItem('mobile_customer_data');
    toast.success(t('auth.logged_out'));
  };

  // Booking
  const handleBookAppointment = async (bookingData) => {
    if (!portalToken) { toast.error(t('auth.please_login')); return false; }
    try {
      let requestData = { ...bookingData };
      if (bookingData.start_time && !bookingData.date) {
        const startTime = bookingData.start_time;
        if (typeof startTime === 'string') {
          if (startTime.includes('T')) {
            const [datePart, timePart] = startTime.split('T');
            requestData.date = datePart;
            requestData.time = timePart.substring(0, 5);
          } else if (startTime.includes(' ')) {
            const [datePart, timePart] = startTime.split(' ');
            requestData.date = datePart;
            requestData.time = timePart.substring(0, 5);
          }
        }
        delete requestData.start_time;
      }
      const response = await axios.post(`${API}/portal/booking/book`, requestData,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      toast.success(t('bookings.booked'));
      await loadCustomerData(portalToken);
      return response.data;
    } catch (error) {
      const det = error.response?.data?.detail;
      if (det && typeof det === 'object' && det.code === 'consent_required') {
        toast.error('Bitte zuerst die Einverständnis-Erklärung unterschreiben (Profil → Einverständnis-Erklärungen)');
      } else {
        toast.error(typeof det === 'string' ? det : t('bookings.booking_failed'));
      }
      return false;
    }
  };

  // Body tracking
  const handleAddBodyEntry = async (data) => {
    if (!portalToken) { toast.error(t('auth.please_login')); return false; }
    setBodyLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('weight', data.weight);
      if (data.height) params.append('height', data.height);
      if (data.body_fat_percent) params.append('body_fat_percent', data.body_fat_percent);
      if (data.muscle_mass) params.append('muscle_mass', data.muscle_mass);
      await axios.post(`${API}/portal/body-tracking?${params.toString()}`, {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      const headers = { Authorization: `Bearer ${portalToken}` };
      const [entriesRes, latestRes, progressRes] = await Promise.all([
        axios.get(`${API}/portal/body-tracking`, { headers }),
        axios.get(`${API}/portal/body-tracking/latest`, { headers }),
        axios.get(`${API}/portal/body-tracking/progress`, { headers }),
      ]);
      setBodyData({
        entries: entriesRes.data?.entries || [],
        latest: latestRes.data,
        progress: progressRes.data
      });
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
      return false;
    } finally {
      setBodyLoading(false);
    }
  };

  const refreshBodyData = async () => {
    if (!portalToken) return;
    setBodyLoading(true);
    try {
      const headers = { Authorization: `Bearer ${portalToken}` };
      const [entriesRes, latestRes, progressRes] = await Promise.all([
        axios.get(`${API}/portal/body-tracking`, { headers }),
        axios.get(`${API}/portal/body-tracking/latest`, { headers }),
        axios.get(`${API}/portal/body-tracking/progress`, { headers }),
      ]);
      setBodyData({
        entries: entriesRes.data?.entries || [],
        latest: latestRes.data,
        progress: progressRes.data
      });
    } catch (error) {
      console.error('Error refreshing body data:', error);
    } finally {
      setBodyLoading(false);
    }
  };

  // Nutrition FAB actions
  const handleNutritionAction = (action) => {
    setNutritionMealType('lunch');
    setFabMenuOpen(false);
    switch (action) {
      case 'photo': setShowPhotoAnalyzer(true); break;
      case 'search': setShowFoodSearch(true); break;
      case 'barcode': setShowBarcodeScanner(true); break;
      case 'create': setShowCreateMeal(true); break;
      case 'new_product': setShowCreateProduct(true); break;
      case 'ai_help': setShowMealCalculator(true); break;
      case 'voice': setShowVoiceInput(true); break;
      case 'planner': setShowMealPlanner(true); break;
      default: break;
    }
  };

  const handleNutritionFoodAdded = useCallback(() => {
    setNutritionRefreshKey(prev => prev + 1);
  }, []);

  // Cancel appointment
  const handleCancelAppointment = async (appointmentId) => {
    if (!portalToken) return false;
    try {
      await axios.post(`${API}/portal/appointments/${appointmentId}/cancel`, {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      toast.success(t('bookings.cancelled'));
      await loadCustomerData(portalToken);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bookings.cancel_failed'));
      return false;
    }
  };

  // Helpers
  const getCustomerFirstName = () => {
    if (customerData?.customer?.first_name) return customerData.customer.first_name;
    if (customerData?.customer?.name) return customerData.customer.name.split(' ')[0];
    return 'Kunde';
  };
  const getCustomerFullName = () => {
    if (customerData?.customer?.first_name && customerData?.customer?.last_name)
      return `${customerData.customer.first_name} ${customerData.customer.last_name}`;
    if (customerData?.customer?.name) return customerData.customer.name;
    return 'Kunde';
  };

  // Invoice handlers
  const handlePayInvoice = async (invoice) => {
    if (!portalToken) { toast.error('Bitte zuerst anmelden'); return; }
    try {
      const response = await axios.post(`${API}/portal/invoices/${invoice.id}/pay`, {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      const data = response.data;
      if (data.payment_link) {
        toast.success('Zahlungsseite wird geöffnet...');
        window.open(data.payment_link, '_blank');
      } else if (data.can_pay_with_credit) {
        const confirmPay = window.confirm(
          `Mit Guthaben bezahlen?\n\nRechnung: CHF ${data.invoice_total.toFixed(2)}\nIhr Guthaben: CHF ${data.credit_balance.toFixed(2)}`
        );
        if (confirmPay) {
          const payResponse = await axios.post(`${API}/portal/invoices/${invoice.id}/pay-with-credit`, {},
            { headers: { Authorization: `Bearer ${portalToken}` } }
          );
          toast.success(payResponse.data.message);
          await loadCustomerData(portalToken);
        }
      } else {
        toast.info(data.message || 'Bitte kontaktieren Sie das Studio');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Zahlung fehlgeschlagen');
    }
  };

  const handleViewInvoice = async (invoice) => {
    if (portalToken) {
      window.open(`${API}/portal/invoices/${invoice.id}/pdf`, '_blank');
    }
  };

  // Navigation
  const handleNavigate = (screen) => {
    setCurrentScreen(screen);
    if (['home', 'nutrition', 'bookings', 'body', 'profile'].includes(screen)) {
      setSelectedTab(screen);
    }
    setFabMenuOpen(false);
  };

  // Register global callbacks for push notifications
  useEffect(() => {
    window.__showPushToast = (title, body) => {
      toast(title, { description: body });
    };
    window.__navigateMobile = handleNavigate;
    return () => {
      delete window.__showPushToast;
      delete window.__navigateMobile;
    };
  }, []);

  // Show bottom nav?
  const hideNav = currentScreen === 'splash' || currentScreen === 'login';
  const showFab = currentScreen === 'nutrition' || currentScreen === 'bookings';

  // Render current screen
  const renderScreen = () => {
    switch (currentScreen) {
      case 'splash':
        return (
          <SplashScreen
            onComplete={() => setCurrentScreen(isLoggedIn ? 'home' : 'login')}
            winterHatEnabled={winterHatEnabled}
            splashVideoUrl={appSettings?.branding?.splash_video_url || '/assets/intro-video.mp4'}
            splashLogoSize={appSettings?.branding?.splash_logo_size || 280}
            splashFrameSize={appSettings?.branding?.splash_frame_size || 110}
          />
        );
      case 'login':
        return (
          <LoginScreen
            onLogin={handleAppLogin}
            loginError={loginError}
            setLoginError={setLoginError}
            winterHatEnabled={winterHatEnabled}
            logoUrl={appSettings?.branding?.logo_dark_url || '/assets/invictafit-logo.png'}
            logoSize={appSettings?.branding?.logo_size || 100}
            logoType="image"
          />
        );
      case 'home':
        return (
          <HomeScreen
            customerName={getCustomerFirstName()}
            onNavigate={handleNavigate}
            points={pointsData.balance}
            creditBalance={pointsData.credit_balance}
            nextAppointment={upcomingAppointments[0]}
            packages={packages}
            winterHatEnabled={winterHatEnabled}
            portalToken={portalToken}
            pendingFeedback={pendingFeedback}
            onOpenFeedback={setFeedbackAppointment}
          />
        );
      case 'nutrition':
        if (nutritionAccess?.has_access === false) {
          return (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center" data-testid="nutrition-locked-screen">
              <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mb-4">
                <Utensils className="w-10 h-10 text-zinc-600" />
              </div>
              <h2 className="text-white text-lg font-semibold mb-2">{t('nutrition.locked_title')}</h2>
              <p className="text-zinc-400 text-sm mb-4">
                {nutritionAccess.message || t('nutrition.locked_message')}
              </p>
              {nutritionAccess.total_completed !== undefined && (
                <div className="w-full max-w-xs">
                  <div className="w-full bg-zinc-700 rounded-full h-2 mb-2">
                    <div 
                      className="h-2 rounded-full bg-yellow-500 transition-all"
                      style={{ width: `${Math.min(100, (nutritionAccess.total_completed / 10) * 100)}%` }}
                    />
                  </div>
                  <p className="text-zinc-500 text-xs">{t('nutrition.trainings_completed', { completed: nutritionAccess.total_completed })}</p>
                </div>
              )}
            </div>
          );
        }
        return (
          <NutritionScreen
            key={nutritionRefreshKey}
            onNavigate={handleNavigate}
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
          />
        );
      case 'bookings':
        return (
          <BookingsScreen
            appointments={appointments}
            upcomingAppointments={upcomingAppointments}
            onCancelAppointment={handleCancelAppointment}
            onRefresh={refreshData}
            portalToken={portalToken}
          />
        );
      case 'body':
        return (
          <BodyScreen
            bodyData={bodyData}
            onAddEntry={handleAddBodyEntry}
            onRefresh={refreshBodyData}
            loading={bodyLoading}
            portalToken={portalToken}
          />
        );
      case 'profile':
        return (
          <ProfileScreen
            customerFirstName={getCustomerFirstName()}
            customerFullName={getCustomerFullName()}
            customerPoints={pointsData.balance}
            creditBalance={pointsData.credit_balance}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
          />
        );
      case 'account':
        return (
          <AccountScreen
            points={pointsData.balance}
            creditBalance={pointsData.credit_balance}
            transactions={pointsData.transactions}
            packages={packages}
            invoices={invoices}
            onNavigate={handleNavigate}
            onPayInvoice={handlePayInvoice}
            onViewInvoice={handleViewInvoice}
          />
        );
      case 'invoices':
        return (
          <AccountScreen
            points={pointsData.balance}
            creditBalance={pointsData.credit_balance}
            transactions={pointsData.transactions}
            packages={packages}
            invoices={invoices}
            onNavigate={handleNavigate}
            onPayInvoice={handlePayInvoice}
            onViewInvoice={handleViewInvoice}
            initialTab="invoices"
          />
        );
      case 'shop':
        return (
          <ShopScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'health':
        return <HealthScreen />;
      case 'customer-profile':
      case 'payment-methods':
        return (
          <CustomerProfileScreen
            customer={customerData?.customer || customerData}
            paymentMethods={[]}
            onBack={() => setCurrentScreen('profile')}
            onAddPaymentMethod={() => toast.info('Zahlungsmethode hinzufügen - In Entwicklung')}
            onDeletePaymentMethod={() => toast.info('Zahlungsmethode entfernen - In Entwicklung')}
            onSetDefaultPaymentMethod={() => toast.info('Standard setzen - In Entwicklung')}
            portalToken={portalToken}
            onProfileUpdate={() => {
              if (portalToken) loadCustomerData(portalToken);
            }}
          />
        );
      case 'fitness':
        return (
          <FitnessScreen
            portalToken={portalToken}
            customerData={customerData}
            bodyData={bodyData}
            onAddBodyEntry={() => toast.info('Körperdaten können in den Einstellungen hinzugefügt werden')}
            onBack={() => setCurrentScreen('home')}
          />
        );
      case 'sleep':
        return <SleepScreen />;
      case 'community':
        return (
          <CommunityScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
          />
        );
      case 'recipes':
        return (
          <RecipesScreen
            portalToken={portalToken}
            onNavigate={handleNavigate}
          />
        );
      case 'shopping':
        return <ShoppingScreen />;
      case 'booking-new':
        return (
          <BookingNewScreen
            onBack={() => setCurrentScreen('bookings')}
            services={services}
            trainers={trainers}
            portalToken={portalToken}
            onBook={handleBookAppointment}
            customerData={customerData}
          />
        );
      case 'waitlist':
        return (
          <WaitlistScreen
            onBack={() => setCurrentScreen('bookings')}
            portalToken={portalToken}
            services={services}
          />
        );
      case 'health-coach':
        return (
          <HealthCoachScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'documents':
        return (
          <DocumentsScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'periodization':
        return (
          <PeriodizationScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'recovery':
        return (
          <RecoveryScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'transaction-history':
        return (
          <TransactionHistoryScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'notification-settings':
        return (
          <NotificationSettingsScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'subscriptions':
        return (
          <SubscriptionsScreen
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'training-analytics':
        return (
          <TrainingAnalyticsScreen
            portalToken={portalToken}
            onNavigate={handleNavigate}
          />
        );
      case 'photo-history':
        return (
          <PhotoHistoryScreen
            portalToken={portalToken}
            onNavigate={handleNavigate}
          />
        );
      case 'settings':
        return (
          <AppSettingsScreen
            portalToken={portalToken}
            onBack={() => handleNavigate('profile')}
            onNavigate={handleNavigate}
          />
        );
      default:
        return <HomeScreen customerName={getCustomerFirstName()} onNavigate={handleNavigate} />;
    }
  };

  const tabs = [
    { id: 'home', icon: Home, label: t('nav.home') },
    { id: 'bookings', icon: Calendar, label: t('nav.bookings') },
    { id: 'nutrition', icon: Utensils, label: t('nav.nutrition') },
    { id: 'fitness', icon: Heart, label: t('nav.health') },
    { id: 'profile', icon: Menu, label: t('nav.more') },
  ];

  return (
    <div
      className="fixed inset-0 bg-zinc-950 flex flex-col"
      style={{ 
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      data-testid="mobile-standalone-app"
    >
      {/* Status Bar Area */}
      <div className="h-12 bg-zinc-950 flex items-center justify-between px-6 flex-shrink-0" data-testid="mobile-status-bar">
        <span className="text-white text-sm font-medium">
          {new Date().getHours().toString().padStart(2, '0')}:{new Date().getMinutes().toString().padStart(2, '0')}
        </span>
        <div className="flex items-center gap-1">
          <div className="w-4 h-2 border border-white rounded-sm">
            <div className="w-3 h-1.5 bg-yellow-500 rounded-sm" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 relative overflow-hidden">
        {/* Seasonal Effects */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          {snowEnabled && <SnowEffect intensity={snowIntensity} speed={snowSpeed} enabled={snowEnabled} />}
          {confettiEnabled && <ConfettiEffect intensity={confettiIntensity} enabled={confettiEnabled} />}
          {cherryBlossomEnabled && <CherryBlossomEffect intensity={cherryBlossomIntensity} enabled={cherryBlossomEnabled} />}
          {fireworksEnabled && <FireworksEffect intensity={fireworksIntensity} enabled={fireworksEnabled} />}
        </div>

        {/* Screen Content */}
        <div className={`relative h-full overflow-y-auto z-10 ${hideNav ? '' : 'pb-20'}`}>
          {loading ? (
            <ScreenLoader />
          ) : (
            <Suspense fallback={<ScreenLoader />}>
              {renderScreen()}
            </Suspense>
          )}
        </div>

        {/* FAB Menu Overlay */}
        {showFab && fabMenuOpen && currentScreen === 'nutrition' && (
          <div
            className="absolute inset-0 z-40"
            onClick={() => setFabMenuOpen(false)}
            data-testid="mobile-fab-overlay"
          >
            <div className="absolute inset-0 bg-black/60" />
            <div
              className="absolute w-56 bg-zinc-800 rounded-xl overflow-hidden shadow-2xl border border-zinc-700"
              style={{ bottom: '100px', right: '20px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => handleNutritionAction('ai_help')}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-700/50 transition-colors border-b border-zinc-700/50 bg-gradient-to-r from-yellow-500/20 to-orange-500/20"
                data-testid="mobile-fab-ai"
              >
                <div className="w-9 h-9 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="text-yellow-400 text-sm font-medium">{t('fab.ai_calculator')}</span>
              </button>
              <button
                onClick={() => handleNutritionAction('photo')}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-700/50 transition-colors border-b border-zinc-700/50"
                data-testid="mobile-fab-photo"
              >
                <div className="w-9 h-9 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <Camera className="w-4 h-4 text-white" />
                </div>
                <span className="text-white text-sm">{t('fab.photo_meal')}</span>
              </button>
              <button
                onClick={() => handleNutritionAction('search')}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-700/50 transition-colors border-b border-zinc-700/50"
                data-testid="mobile-fab-search"
              >
                <div className="w-9 h-9 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Search className="w-4 h-4 text-white" />
                </div>
                <span className="text-white text-sm">{t('fab.search_food')}</span>
              </button>
              <button
                onClick={() => handleNutritionAction('create')}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-700/50 transition-colors"
                data-testid="mobile-fab-create"
              >
                <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Utensils className="w-4 h-4 text-white" />
                </div>
                <span className="text-white text-sm">{t('fab.create_meal')}</span>
              </button>
            </div>
          </div>
        )}

        {/* FAB Button */}
        {showFab && !hideNav && (
          <button
            onClick={() => {
              if (currentScreen === 'bookings') {
                handleNavigate('booking-new');
              } else {
                setFabMenuOpen(!fabMenuOpen);
              }
            }}
            className={`absolute z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 ${
              fabMenuOpen ? 'bg-yellow-500 rotate-45' : 'bg-yellow-500 hover:bg-yellow-400'
            }`}
            style={{ bottom: '88px', right: '20px' }}
            data-testid="mobile-fab-button"
          >
            <Plus className="w-6 h-6 text-black" strokeWidth={2.5} />
          </button>
        )}

        {/* Fullscreen Modals */}
        {showFoodSearch && (
          <div className="absolute inset-0 z-50 bg-zinc-950">
            <FoodSearchModal
              isOpen={showFoodSearch}
              onClose={() => setShowFoodSearch(false)}
              onSelectFood={handleNutritionFoodAdded}
              onCreateProduct={(barcode) => {
                setShowFoodSearch(false);
                setCreateProductBarcode(barcode || '');
                setShowCreateProduct(true);
              }}
              portalToken={portalToken}
              mealType={nutritionMealType}
            />
          </div>
        )}

        {showBarcodeScanner && (
          <div className="absolute inset-0 z-50 bg-zinc-950">
            <BarcodeScanner
              isOpen={showBarcodeScanner}
              onClose={() => setShowBarcodeScanner(false)}
              onProductFound={handleNutritionFoodAdded}
              onCreateProduct={(barcode) => {
                setShowBarcodeScanner(false);
                setCreateProductBarcode(barcode || '');
                setShowCreateProduct(true);
              }}
              portalToken={portalToken}
              mealType={nutritionMealType}
            />
          </div>
        )}

        {showPhotoAnalyzer && (
          <div className="absolute inset-0 z-50 bg-zinc-950">
            <MealPhotoAnalyzer
              isOpen={showPhotoAnalyzer}
              onClose={() => setShowPhotoAnalyzer(false)}
              onMealAdded={handleNutritionFoodAdded}
              portalToken={portalToken}
              customerId={customerData?.customer?.id}
              mealType={nutritionMealType}
            />
          </div>
        )}

        {showCreateMeal && (
          <div className="absolute inset-0 z-50 bg-zinc-950">
            <CreateMealModal
              isOpen={showCreateMeal}
              onClose={() => setShowCreateMeal(false)}
              onMealCreated={handleNutritionFoodAdded}
              portalToken={portalToken}
              customerId={customerData?.customer?.id}
            />
          </div>
        )}

        {showCreateProduct && (
          <div className="absolute inset-0 z-50 bg-zinc-950">
            <CreateProductWizard
              isOpen={showCreateProduct}
              onClose={() => { setShowCreateProduct(false); setCreateProductBarcode(''); }}
              onProductCreated={handleNutritionFoodAdded}
              portalToken={portalToken}
              initialBarcode={createProductBarcode}
            />
          </div>
        )}

        {showMealPlanner && (
          <div className="absolute inset-0 z-50 bg-zinc-950">
            <MealPlannerScreen
              portalToken={portalToken}
              customerId={customerData?.customer?.id}
              onClose={() => setShowMealPlanner(false)}
            />
          </div>
        )}

        {showVoiceInput && (
          <div className="absolute inset-0 z-50 bg-zinc-950">
            <VoiceInputModal
              isOpen={showVoiceInput}
              onClose={() => setShowVoiceInput(false)}
              portalToken={portalToken}
              onFoodAdded={handleNutritionFoodAdded}
            />
          </div>
        )}

        {showMealCalculator && (
          <div className="absolute inset-0 z-50 bg-zinc-950">
            <MealCalculatorModal
              isOpen={showMealCalculator}
              onClose={() => setShowMealCalculator(false)}
              portalToken={portalToken}
              customerId={customerData?.customer?.id}
              onMealAdded={handleNutritionFoodAdded}
            />
          </div>
        )}

        {/* Legal Modal */}
        {showLegalModal && legalStatus && (
          <div className="absolute inset-0 z-50">
            <MobileLegalModal
              legalStatus={legalStatus}
              onAccept={handleAcceptLegal}
              onDecline={() => {
                setShowLegalModal(false);
                toast.error('Sie müssen die Bedingungen akzeptieren');
                handleLogout();
              }}
            />
          </div>
        )}

        {/* Feedback Modal */}
        {feedbackAppointment && (
          <FeedbackScreen
            appointment={feedbackAppointment}
            portalToken={portalToken}
            onClose={() => setFeedbackAppointment(null)}
            onSubmitted={() => {
              setPendingFeedback(prev => prev.filter(a => a.id !== feedbackAppointment.id));
              setFeedbackAppointment(null);
            }}
          />
        )}
      </div>

      {/* Bottom Navigation */}
      {!hideNav && (
        <div
          className="flex-shrink-0 bg-zinc-900/95 backdrop-blur-lg border-t border-zinc-800 flex items-center justify-around px-2"
          style={{ 
            height: '72px',
            paddingBottom: 'env(safe-area-inset-bottom, 8px)',
          }}
          data-testid="mobile-bottom-nav"
        >
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setSelectedTab(tab.id); handleNavigate(tab.id); }}
              className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors min-w-[56px] ${
                selectedTab === tab.id ? 'text-yellow-500' : 'text-zinc-500'
              }`}
              data-testid={`mobile-tab-${tab.id}`}
            >
              <tab.icon className="w-6 h-6" />
              <span className="text-[10px] font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      <Toaster position="top-center" />
    </div>
  );
}
